// Enums
// Feature flags
import { APP_FEATURES, AppFeaturesKeys } from '@feature-flags';
import { AuthPermission } from '@q6cyber/proto2es/generated/auth';
import { RoutesEnum } from '@shared/enums';
// Layouts
import { MainComponent } from '@shared/ui';

import { Route } from '@angular/router';

// Guards
import {
  featureFlagGuard,
  pendingChangesGuard,
  permissionsGuard,
  redirectLoggedIn,
  redirectUnauthorizedToLogin,
} from './shared/guards';

// Every activity-log page shares the same guard/component wiring; only permission, feature flag, path and route data differ.
const createActivityLogRoute = ({
  path,
  permission,
  featureFlag,
  type,
  tableId,
}: {
  path: string;
  permission: string[];
  featureFlag: AppFeaturesKeys;
  type: string;
  tableId: string;
}): Route => ({
  path,
  data: {
    permission,
    featureFlag,
    activityLog: { type, tableId },
  },
  canActivate: [permissionsGuard, featureFlagGuard],
  loadComponent: () =>
    import('./features/activity-log/pages/activity-log-page/activity-log-page.component').then(
      c => c.ActivityLogPageComponent
    ),
});

export const appRoutes: Route[] = [
  {
    path: RoutesEnum.ROOT,
    component: MainComponent,
    canActivate: [redirectUnauthorizedToLogin],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/home/pages/home.component').then(
            c => c.HomeComponent
          ),
      },
      {
        path: RoutesEnum.SAMPLE,
        canActivate: [featureFlagGuard],
        data: { featureFlag: APP_FEATURES.show_sample_page },
        loadComponent: () =>
          import('./features/sample/pages/sample.component').then(
            c => c.SampleComponent
          ),
      },
      {
        path: RoutesEnum.WRAPPED,
        canActivate: [featureFlagGuard],
        data: { featureFlag: APP_FEATURES.show_yearly_wrapped },
        loadComponent: () =>
          import('./features/wrapped/pages/wrapped.component').then(
            c => c.WrappedComponent
          ),
      },
      {
        path: RoutesEnum.USER_MANAGEMENT,
        data: {
          permission: [
            AuthPermission[AuthPermission.TEAM_MANAGER],
            AuthPermission[AuthPermission.TEAM_MANAGER_VIEW],
          ],
          featureFlag: APP_FEATURES.show_user_management,
        },
        canActivate: [permissionsGuard, featureFlagGuard],
        canDeactivate: [pendingChangesGuard],
        loadComponent: () =>
          import('./features/user-management/pages/user-management.component').then(
            c => c.UserManagementComponent
          ),
      },
      createActivityLogRoute({
        path: RoutesEnum.USER_MANAGEMENT + '/:id/activity-log',
        permission: [
          AuthPermission[AuthPermission.TEAM_MANAGER],
          AuthPermission[AuthPermission.TEAM_MANAGER_VIEW],
        ],
        featureFlag: APP_FEATURES.show_user_management,
        type: 'userManagement',
        tableId: 'user-management-activity-log-table',
      }),
      {
        path: RoutesEnum.DIRECT_COLLECTION,
        data: {
          permission: AuthPermission[AuthPermission.DIRECT_COLLECT_VIEW],
          featureFlag: APP_FEATURES.show_direct_collect_viewer,
        },
        canActivate: [permissionsGuard, featureFlagGuard],
        loadComponent: () =>
          import('./features/direct-collection/pages/direct-collection.component').then(
            c => c.DirectCollectionComponent
          ),
      },
      {
        path: RoutesEnum.FRAUD_ALERTS,
        data: {
          permission: [
            AuthPermission[AuthPermission.FRAUD_ALERTS_VIEW],
            AuthPermission[AuthPermission.FRAUD_ALERTS_WRITE],
            AuthPermission[AuthPermission.FRAUD_ALERTS_ADMIN],
          ],
          featureFlag: APP_FEATURES.show_fraud_alerts,
        },
        canActivate: [permissionsGuard, featureFlagGuard],
        loadComponent: () =>
          import('./features/fraud-alerts/pages/fraud-alerts.component').then(
            c => c.FraudAlertsComponent
          ),
      },
      {
        path: RoutesEnum.FRAUD_ALERTS + '/:id/detail-view',
        data: {
          permission: [
            AuthPermission[AuthPermission.FRAUD_ALERTS_VIEW],
            AuthPermission[AuthPermission.FRAUD_ALERTS_WRITE],
            AuthPermission[AuthPermission.FRAUD_ALERTS_ADMIN],
          ],
          featureFlag: APP_FEATURES.show_fraud_alerts,
        },
        canActivate: [permissionsGuard, featureFlagGuard],
        canDeactivate: [pendingChangesGuard],
        loadComponent: () =>
          import('./features/fraud-alerts/pages/fraud-alerts-detail-view/fraud-alerts-detail-view.component').then(
            c => c.FraudAlertsDetailViewComponent
          ),
      },
      createActivityLogRoute({
        path: RoutesEnum.FRAUD_ALERTS + '/:id/activity-log',
        permission: [
          AuthPermission[AuthPermission.FRAUD_ALERTS_VIEW],
          AuthPermission[AuthPermission.FRAUD_ALERTS_WRITE],
          AuthPermission[AuthPermission.FRAUD_ALERTS_ADMIN],
        ],
        featureFlag: APP_FEATURES.show_fraud_alerts,
        type: 'fraudAlerts',
        tableId: 'fraud-alerts-activity-log-table',
      }),
      createActivityLogRoute({
        path: RoutesEnum.CARDS + '/auto-purchase/:id/activity-log',
        permission: [
          AuthPermission[AuthPermission.CARDS_AUTO_PURCHASE_VIEW],
          AuthPermission[AuthPermission.CARDS_AUTO_PURCHASE_WRITE],
        ],
        featureFlag: APP_FEATURES.show_cards_auto_purchase,
        type: 'cardsAutoPurchase',
        tableId: 'cards-auto-purchase-activity-log-table',
      }),
      {
        path: RoutesEnum.CARDS + '/auto-purchase',
        data: {
          permission: [
            AuthPermission[AuthPermission.CARDS_AUTO_PURCHASE_VIEW],
            AuthPermission[AuthPermission.CARDS_AUTO_PURCHASE_WRITE],
          ],
          featureFlag: APP_FEATURES.show_cards_auto_purchase,
        },
        canActivate: [permissionsGuard, featureFlagGuard],
        canDeactivate: [pendingChangesGuard],
        loadComponent: () =>
          import('./features/cards/cards-auto-purchase/pages/automated-purchase/cards-auto-purchase').then(
            c => c.CardsAutoPurchaseComponent
          ),
      },
      createActivityLogRoute({
        path: RoutesEnum.CARDS + '/feedback/activity-log',
        permission: [AuthPermission[AuthPermission.CARDS_FEEDBACK_WRITE]],
        featureFlag: APP_FEATURES.show_cards_feedback,
        type: 'cardsFeedback',
        tableId: 'cards-feedback-activity-log-table',
      }),
      {
        path: RoutesEnum.CARDS + '/feedback',
        data: {
          permission: [AuthPermission[AuthPermission.CARDS_FEEDBACK_WRITE]],
          featureFlag: APP_FEATURES.show_cards_feedback,
        },
        canActivate: [permissionsGuard, featureFlagGuard],
        loadComponent: () =>
          import('./features/cards/cards-feedback/pages/cards-feedback/cards-feedback.component').then(
            c => c.CardsFeedbackComponent
          ),
      },
      createActivityLogRoute({
        path: RoutesEnum.CARDS + '/new/file-import/activity-log',
        permission: [AuthPermission[AuthPermission.CARDS_UPLOAD_WRITE]],
        featureFlag: APP_FEATURES.show_cards_upload,
        type: 'cardsFileImport',
        tableId: 'cards-file-import-activity-log-table',
      }),
      createActivityLogRoute({
        path: RoutesEnum.CARDS + '/new/bulk-upload/activity-log',
        permission: [AuthPermission[AuthPermission.CARDS_UPLOAD_WRITE]],
        featureFlag: APP_FEATURES.show_cards_upload,
        type: 'cardsBulkUpload',
        tableId: 'cards-bulk-upload-activity-log-table',
      }),
      {
        path: RoutesEnum.CARDS + '/new',
        data: {
          permission: [AuthPermission[AuthPermission.CARDS_UPLOAD_WRITE]],
          featureFlag: APP_FEATURES.show_cards_upload,
        },
        canActivate: [permissionsGuard, featureFlagGuard],
        loadComponent: () =>
          import('./features/cards/cards-upload/pages/cards-upload/cards-upload.component').then(
            c => c.CardsUploadComponent
          ),
      },
      {
        path: RoutesEnum.BOTNET_REPORTS,
        data: {
          permission: AuthPermission[AuthPermission.BNR_VIEW],
          featureFlag: APP_FEATURES.show_bnr,
        },
        canActivate: [permissionsGuard, featureFlagGuard],
        loadComponent: () =>
          import('./features/data-viewer/pages/botnet-reports/botnet-reports.component').then(
            c => c.BotnetReportsComponent
          ),
      },
      {
        path: RoutesEnum.INFECTIONS,
        data: {
          permission: AuthPermission[AuthPermission.INFECTIONS_VIEW],
          featureFlag: APP_FEATURES.show_infections,
        },
        canActivate: [permissionsGuard, featureFlagGuard],
        loadComponent: () =>
          import('./features/data-viewer/pages/infections/infections.component').then(
            c => c.InfectionsComponent
          ),
      },
      {
        path: RoutesEnum.THREAT_ENTITY,
        data: {
          permission: [
            AuthPermission[AuthPermission.THREAT_ENTITY_VIEW],
            AuthPermission[AuthPermission.THREAT_ENTITY_WRITE],
          ],
          featureFlag: APP_FEATURES.show_threat_entity,
        },
        canActivate: [permissionsGuard, featureFlagGuard],
        canDeactivate: [pendingChangesGuard],
        loadComponent: () =>
          import('./features/threat-entity/pages/threat-entity.component').then(
            c => c.ThreatEntityComponent
          ),
      },
      {
        path: RoutesEnum.CLIENT,
        loadComponent: () =>
          import('./features/client/pages/client.component').then(
            c => c.ClientComponent
          ),
        children: [
          {
            path: RoutesEnum.CLIENT_METRICS,
            data: {
              permission: AuthPermission[AuthPermission.CLIENT_METRICS_VIEW],
              featureFlag: APP_FEATURES.show_client_metrics,
            },
            canActivate: [permissionsGuard, featureFlagGuard],
            canDeactivate: [pendingChangesGuard],
            loadComponent: () =>
              import('./features/client/client-metrics/pages/client-metrics.component').then(
                c => c.ClientMetricsComponent
              ),
          },
          {
            path: RoutesEnum.CLIENT_SUBSCRIPTIONS,
            data: {
              permission: [
                AuthPermission[AuthPermission.CLIENT_SUBSCRIPTIONS_VIEW],
                AuthPermission[AuthPermission.CLIENT_SUBSCRIPTIONS_WRITE],
              ],
              featureFlag: APP_FEATURES.show_client_subscriptions,
            },
            canActivate: [permissionsGuard, featureFlagGuard],
            canDeactivate: [pendingChangesGuard],
            loadComponent: () =>
              import('./features/client/client-subscriptions/pages/client-subscriptions.component').then(
                c => c.ClientSubscriptionsComponent
              ),
          },
          createActivityLogRoute({
            path: RoutesEnum.CLIENT_SUBSCRIPTIONS + '/:id/activity-log',
            permission: [
              AuthPermission[AuthPermission.CLIENT_SUBSCRIPTIONS_VIEW],
              AuthPermission[AuthPermission.CLIENT_SUBSCRIPTIONS_WRITE],
            ],
            featureFlag: APP_FEATURES.show_client_subscriptions,
            type: 'clientSubscriptions',
            tableId: 'client-subscriptions-activity-log-table',
          }),
          {
            path: RoutesEnum.CLIENT_SUBSCRIPTION_LIST,
            data: {
              permission: [
                AuthPermission[AuthPermission.CLIENT_SUBSCRIPTIONS_VIEW],
                AuthPermission[AuthPermission.CLIENT_SUBSCRIPTIONS_WRITE],
              ],
              featureFlag: APP_FEATURES.show_client_subscriptions,
            },
            canActivate: [permissionsGuard, featureFlagGuard],
            canDeactivate: [pendingChangesGuard],
            loadComponent: () =>
              import('./features/client/client-subscription-list/pages/client-subscription-list.component').then(
                c => c.ClientSubscriptionListComponent
              ),
          },
          {
            path: '',
            redirectTo: RoutesEnum.CLIENT_METRICS,
            pathMatch: 'full',
          },
        ],
      },
      createActivityLogRoute({
        path: `${RoutesEnum.BREACH_INFO}/:id/activity-log`,
        permission: [
          AuthPermission[AuthPermission.BREACH_VIEW],
          AuthPermission[AuthPermission.BREACH_WRITE],
        ],
        featureFlag: APP_FEATURES.show_breach_info,
        type: 'breachInfo',
        tableId: 'breach-info-activity-log-table',
      }),
      {
        path: RoutesEnum.BREACH_INFO,
        data: {
          permission: [
            AuthPermission[AuthPermission.BREACH_VIEW],
            AuthPermission[AuthPermission.BREACH_WRITE],
          ],
          featureFlag: APP_FEATURES.show_breach_info,
        },
        canActivate: [permissionsGuard, featureFlagGuard],
        canDeactivate: [pendingChangesGuard],
        loadComponent: () =>
          import('./features/breach-info/pages/breach-info.component').then(
            c => c.BreachInfoComponent
          ),
      },
      {
        path: RoutesEnum.SOURCES,
        data: {
          permission: [
            AuthPermission[AuthPermission.SOURCE_VIEW],
            AuthPermission[AuthPermission.SOURCE_WRITE],
          ],
          featureFlag: APP_FEATURES.show_sources,
        },
        canActivate: [permissionsGuard, featureFlagGuard],
        canDeactivate: [pendingChangesGuard],
        loadComponent: () =>
          import('./features/sources/pages/sources.component').then(
            c => c.SourcesComponent
          ),
      },
      {
        path: RoutesEnum.ALERT_BUILDER,
        data: {
          permission: [
            AuthPermission[AuthPermission.ALERT_BUILDER_VIEW],
            AuthPermission[AuthPermission.ALERT_BUILDER_WRITE],
          ],
          featureFlag: APP_FEATURES.show_alert_builder,
        },
        canActivate: [permissionsGuard, featureFlagGuard],
        canDeactivate: [pendingChangesGuard],
        loadComponent: () =>
          import('./features/alert-builder/pages/alert-builder.component').then(
            c => c.AlertBuilderComponent
          ),
      },
      createActivityLogRoute({
        path: RoutesEnum.ALERT_BUILDER + '/:id/activity-log',
        permission: [
          AuthPermission[AuthPermission.ALERT_BUILDER_VIEW],
          AuthPermission[AuthPermission.ALERT_BUILDER_WRITE],
        ],
        featureFlag: APP_FEATURES.show_alert_builder,
        type: 'alertBuilder',
        tableId: 'alert-builder-activity-log-table',
      }),
      createActivityLogRoute({
        path: `${RoutesEnum.BANNER_MANAGER}/:id/activity-log`,
        permission: [
          AuthPermission[AuthPermission.BANNER_MANAGER_VIEW],
          AuthPermission[AuthPermission.BANNER_MANAGER_WRITE],
        ],
        featureFlag: APP_FEATURES.show_banner_manager,
        type: 'bannerManager',
        tableId: 'banner-manager-activity-log-table',
      }),
      {
        path: RoutesEnum.BANNER_MANAGER,
        data: {
          permission: [
            AuthPermission[AuthPermission.BANNER_MANAGER_VIEW],
            AuthPermission[AuthPermission.BANNER_MANAGER_WRITE],
          ],
          featureFlag: APP_FEATURES.show_banner_manager,
        },
        canActivate: [permissionsGuard, featureFlagGuard],
        canDeactivate: [pendingChangesGuard],
        loadComponent: () =>
          import('./features/banner-manager/pages/banner-manager.component').then(
            c => c.BannerManagerComponent
          ),
      },
      createActivityLogRoute({
        path: `${RoutesEnum.PORTAL_CONFIG}/:id/activity-log`,
        permission: [
          AuthPermission[AuthPermission.PORTAL_VIEW],
          AuthPermission[AuthPermission.PORTAL_WRITE],
          AuthPermission[AuthPermission.PORTAL_ADMIN],
        ],
        featureFlag: APP_FEATURES.show_portal_config,
        type: 'portalConfig',
        tableId: 'portal-config-activity-log-table',
      }),
      {
        path: RoutesEnum.PORTAL_CONFIG,
        data: {
          permission: [
            AuthPermission[AuthPermission.PORTAL_VIEW],
            AuthPermission[AuthPermission.PORTAL_WRITE],
            AuthPermission[AuthPermission.PORTAL_ADMIN],
          ],
          featureFlag: APP_FEATURES.show_portal_config,
        },
        canActivate: [permissionsGuard, featureFlagGuard],
        canDeactivate: [pendingChangesGuard],
        loadComponent: () =>
          import('./features/portal-config/pages/portal-config.component').then(
            c => c.PortalConfigComponent
          ),
      },
      {
        path: RoutesEnum.PORTAL_FAQ,
        data: {
          permission: [
            AuthPermission[AuthPermission.PORTAL_VIEW],
            AuthPermission[AuthPermission.PORTAL_WRITE],
            AuthPermission[AuthPermission.PORTAL_ADMIN],
          ],
          featureFlag: APP_FEATURES.show_portal_config,
        },
        canActivate: [permissionsGuard, featureFlagGuard],
        canDeactivate: [pendingChangesGuard],
        loadComponent: () =>
          import('./features/portal-faq/pages/portal-faq.component').then(
            c => c.PortalFaqComponent
          ),
      },
      createActivityLogRoute({
        path: `${RoutesEnum.FILE_SHARE}/:id/activity-log`,
        permission: [
          AuthPermission[AuthPermission.FILESHARE_VIEW],
          AuthPermission[AuthPermission.FILESHARE_WRITE],
        ],
        featureFlag: APP_FEATURES.show_file_share,
        type: 'fileShare',
        tableId: 'file-share-activity-log-table',
      }),
      {
        path: RoutesEnum.FILE_SHARE,
        data: {
          permission: [
            AuthPermission[AuthPermission.FILESHARE_VIEW],
            AuthPermission[AuthPermission.FILESHARE_WRITE],
          ],
          featureFlag: APP_FEATURES.show_file_share,
        },
        canActivate: [permissionsGuard, featureFlagGuard],
        canDeactivate: [pendingChangesGuard],
        loadComponent: () =>
          import('./features/file-share/pages/file-share.component').then(
            c => c.FileShareComponent
          ),
      },

      {
        path: RoutesEnum.CHECK_RECORDS,
        data: {
          permission: [AuthPermission[AuthPermission.CHECK_RECORD_VIEW]],
          featureFlag: APP_FEATURES.show_check_record_browser,
        },
        canActivate: [permissionsGuard, featureFlagGuard],
        loadComponent: () =>
          import('./features/check-records/pages/check-records.component').then(
            c => c.CheckRecordsComponent
          ),
      },
      {
        path: RoutesEnum.NOT_FOUND,
        loadComponent: () =>
          import('./features/not-found/pages/not-found.component').then(
            c => c.NotFoundComponent
          ),
      },
    ],
  },
  {
    path: RoutesEnum.LOGIN,
    loadComponent: () =>
      import('./features/login/pages/login.component').then(
        c => c.LoginComponent
      ),
    canActivate: [redirectLoggedIn],
  },
  {
    path: RoutesEnum.EMAIL_ACTIONS,
    loadComponent: () =>
      import('./features/email-actions/pages/email-actions.component').then(
        c => c.EmailActionsComponent
      ),
    canActivate: [redirectLoggedIn],
  },
  { path: '**', redirectTo: RoutesEnum.NOT_FOUND },
];
